import { createSelector } from "reselect";
import { RootState } from "@/store";
import { OfferState } from "@/store/slices/jsonld/OfferSlice";

/**
 * Sélecteur pour obtenir l'état des offres depuis le store Redux.
 *
 * @param {RootState} state - L'état global de l'application.
 * @returns {OfferState} L'état des offres.
 */
const selectOffersState = (state: RootState): OfferState => state.offers;
const selectOffersSelectedState = (state: RootState): OfferState => state.offersSelected;
const selectOfferState = (state: RootState): OfferState => state.offer;

/**
 * Sélecteur pour obtenir les différents états de chargement des offres.
 *
 * @returns {object} Un objet avec les états de chargement.
 */
export const selectOffersSelectedLoadingStates = createSelector(
  [selectOffersSelectedState],
  (OffersSelectedState) => ({
    isLoading: OffersSelectedState.loadingSelected === "pending",
    isSucceeded: OffersSelectedState.loadingSelected === "succeeded",
    isFailed: OffersSelectedState.loadingSelected === "failed",
    isIdle: OffersSelectedState.loadingSelected === "idle",
  }),
);

/**
 * Sélecteur pour obtenir les différents états de chargement des offres.
 *
 * @returns {object} Un objet avec les états de chargement.
 */
export const selectOffersLoadingStates = createSelector([selectOffersState], (OfferState) => ({
  isLoading: OfferState.loadingOffers === "pending",
  isSucceeded: OfferState.loadingOffers === "succeeded",
  isFailed: OfferState.loadingOffers === "failed",
  isIdle: OfferState.loadingOffers === "idle",
}));

/**
 * Sélecteur pour obtenir les différents états de chargement des offres.
 *
 * @returns {object} Un objet avec les états de chargement.
 */
export const selectOfferLoadingStates = createSelector([selectOfferState], (OfferState) => ({
  isLoading: OfferState.loadingOffer === "pending",
  isSucceeded: OfferState.loadingOffer === "succeeded",
  isFailed: OfferState.loadingOffer === "failed",
  isIdle: OfferState.loadingOffer === "idle",
}));

/**
 * Sélecteur pour obtenir les données des offres.
 *
 * @returns {TypeOffers} Un tableau des offres.
 */
export const selectOffers = createSelector([selectOffersState], (OfferState) => OfferState.offers);

/**
 * Sélecteur pour obtenir les données des offres sélectionné dans l'administration.
 *
 * @returns {string[]} Un tableau des offres.
 */
export const selectOffersAdminCategoriesSelect = createSelector(
  [selectOffersState],
  (OfferState) => OfferState.categoriesSelect,
);

/**
 * Sélecteur pour obtenir les données des offres sélectionné dans l'administration.
 *
 * @returns {string[]} Un tableau des offres.
 */
export const selectOffersAdminFuelsSelect = createSelector(
  [selectOffersState],
  (OfferState) => OfferState.fuelsSelect,
);

/**
 * Sélecteur pour obtenir les données des offres.
 *
 * @returns {TypeOffers} Un tableau des offres.
 */
export const selectOffersSelected = createSelector(
  [selectOffersSelectedState],
  (OffersSelectedState) => OffersSelectedState.offersSelected,
);

/**
 * Sélecteur pour obtenir une offre spécifique.
 *
 * @returns {TypeOffer | null} L'offre récupérée ou `null'.
 */
export const selectOffer = createSelector([selectOffersState], (OfferState) => OfferState.offer);

/**
 * Sélecteur pour obtenir les erreurs liées au chargement des offres.
 *
 * @returns {string | null} Un message d'erreur, ou `null` s'il n'y a pas d'erreur.
 */
export const selectOfferError = createSelector(
  [selectOffersState],
  (OfferState) => OfferState.error,
);
